import {
  Menu,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { fetchAdminOverview } from "../api/adminOverviewApi";
import { getStoredUser } from "../../utils/auth";

const searchGroups = [
  ["orders", "Orders"],
  ["customers", "Customers"],
  ["menu", "Menu"],
];

function flattenResults(results) {
  return searchGroups.flatMap(([key, label]) => (
    results?.[key] || []
  ).map((result) => ({ ...result, groupKey: key, groupLabel: label })));
}

function AdminHeader({ onOpenSidebar }) {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const user = getStoredUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState({ orders: [], customers: [], menu: [] });
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const displayName = user?.displayName || user?.username || "SugaLumps Staff";
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  const trimmedSearch = searchTerm.trim();
  const flatResults = useMemo(() => flattenResults(results), [results]);
  const showResults = isSearchFocused && Boolean(trimmedSearch);

  useEffect(() => {
    if (!trimmedSearch) {
      setResults({ orders: [], customers: [], menu: [] });
      setHighlightedIndex(-1);
      return undefined;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      try {
        const data = await fetchAdminOverview({ search: trimmedSearch, signal: controller.signal });
        setResults(data.search || { orders: [], customers: [], menu: [] });
        setHighlightedIndex(-1);
      } catch (error) {
        if (error.name !== "AbortError") setResults({ orders: [], customers: [], menu: [], error: true });
      }
    }, 250);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [trimmedSearch]);

  useEffect(() => {
    function closeOnOutsideClick(event) {
      if (!searchRef.current?.contains(event.target)) setIsSearchFocused(false);
    }
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  function openResult(result) {
    if (!result?.path) return;
    setSearchTerm("");
    setIsSearchFocused(false);
    navigate(result.path);
  }

  function handleSearchKeyDown(event) {
    if (!flatResults.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((current) => (current + 1) % flatResults.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((current) => (current <= 0 ? flatResults.length - 1 : current - 1));
    } else if (event.key === "Enter" && highlightedIndex >= 0) {
      event.preventDefault();
      openResult(flatResults[highlightedIndex]);
    } else if (event.key === "Escape") {
      setIsSearchFocused(false);
    }
  }

  return (
    <header className="admin-header">
      <div className="admin-header__left">
        <button type="button" className="admin-header__menu-button" aria-label="Open navigation" onClick={onOpenSidebar}>
          <Menu size={22} />
        </button>

        <div className="admin-header__search" ref={searchRef}>
          <Search size={18} />
          <input
            type="search"
            placeholder="Search orders, customers, or products..."
            aria-label="Search admin"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onKeyDown={handleSearchKeyDown}
          />
          {showResults && (
            <div className="admin-header__search-results" role="listbox">
              {searchGroups.map(([key, label]) => {
                const groupResults = results?.[key] || [];
                if (!groupResults.length) return null;
                return (
                  <div className="admin-header__search-group" key={key}>
                    <span>{label}</span>
                    {groupResults.map((result) => {
                      const flatIndex = flatResults.findIndex((item) => item.groupKey === key && item.id === result.id);
                      return (
                        <button
                          type="button"
                          key={`${key}-${result.id}`}
                          className={flatIndex === highlightedIndex ? "is-highlighted" : ""}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => openResult(result)}
                          role="option"
                          aria-selected={flatIndex === highlightedIndex}
                        >
                          <strong>{result.label}</strong>
                          {result.description && <small>{result.description}</small>}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              {!flatResults.length && <p className="admin-header__search-empty">No matches found.</p>}
            </div>
          )}
        </div>
      </div>

      <div className="admin-header__actions">
        <div className="admin-header__profile" aria-label="Signed in admin">
          <span className="admin-header__avatar">{initials || "SL"}</span>
          <span className="admin-header__profile-copy">
            <strong>{displayName}</strong>
            <span>{user?.role === "owner" ? "Owner" : "Staff"}</span>
          </span>
        </div>
      </div>
    </header>
  );
}

export default AdminHeader;
